import { $convertToMarkdownString, TRANSFORMERS } from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { Box } from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { AutosaveContext } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/AutosaveContext";
import AutoSavePlugin from "src/components/DocViewer/components/markdownDocViewer/rawEditor/AutoSavePlugin";
import { BASE_EDITOR_NODES } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/editorNodes";
import {
  ImageUploadPlugin,
  ImageUploadProvider,
} from "src/components/DocViewer/components/markdownDocViewer/rawEditor/imageUploadPlugin";
import MarkdownPastePlugin from "src/components/DocViewer/components/markdownDocViewer/rawEditor/markdownPastePlugin/MarkdownPastePlugin";
import { PLAYGROUND_TRANSFORMERS } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/markdownTransformers";
import {
  importMarkdownToLexical,
  normalizeExportedMarkdown,
} from "src/components/DocViewer/components/markdownDocViewer/rawEditor/preprocessMarkdown";
import TabFocusPlugin from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tabFocusPlugin/tabFocusPlugin";
import { TabIndentationPlugin } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tabIndentationPlugin/tabIndentationPlugin";
import TableActionMenuPlugin from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tablePlugin/TableActionMenuPlugin";
import TableCellLineBreakPlugin from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tablePlugin/TableCellLineBreakPlugin";
import TableCellResizer from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tablePlugin/TableCellResizer";
import TableCellSelectionPlugin from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tablePlugin/TableCellSelectionPlugin";
import TableHoverActionsPlugin from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tablePlugin/TableHoverActionsPlugin";
import { TableContext } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tablePlugin/TablePlugin";
import ToolbarContainer from "src/components/DocViewer/components/markdownDocViewer/rawEditor/ToolbarContainer";
import Theme from "./Theme";

export const editorConfig = {
  namespace: "editor",
  nodes: [...BASE_EDITOR_NODES],
  onError(error: Error) {
    throw error;
  },
  theme: Theme,
  editable: true,
};

export interface RawEditorHandle {
  reloadContent: (markdown: string) => void;
  getMarkdownContent: () => string;
}

interface RawEditorProps {
  tableStyles?: any;
  onChange: (value: string) => void;
  hideToolbar?: boolean;
  markdown: string;
  removeBorder?: boolean;
  isReadOnly?: boolean;
  autoFocus?: boolean;
  children?: React.ReactNode;
  placeholder?: string;
}

const RawEditorInner = forwardRef<RawEditorHandle, RawEditorProps>(
  (
    {
      tableStyles,
      onChange,
      hideToolbar,
      markdown,
      removeBorder,
      isReadOnly = false,
      autoFocus = true,
      children,
      placeholder,
    },
    ref
  ) => {
    const { orgId = "" } = useParams();
    if (!orgId) {
      throw new Error("Org ID is required");
    }
    const triggerAutosaveRef = useRef<(() => void) | null>(null);
    const [isLocalAutosaving, setIsAutosaving] = useState(false);

    const triggerAutosave = useCallback(() => {
      if (triggerAutosaveRef.current) {
        triggerAutosaveRef.current();
      }
    }, []);

    const [editor] = useLexicalComposerContext();

    const getCurrentMarkdownContent = useCallback(() => {
      let markdown = "";
      editor.read(() => {
        const rawMarkdown = $convertToMarkdownString(PLAYGROUND_TRANSFORMERS);
        markdown = normalizeExportedMarkdown(rawMarkdown);
      });
      return markdown;
    }, [editor]);

    const persistEditorMarkdown = useCallback(
      async (nextMarkdown: string) => {
        await onChange(nextMarkdown);
      },
      [onChange]
    );

    const handleEditorChange = useCallback(
      (nextMarkdown: string) => {
        void persistEditorMarkdown(nextMarkdown).catch((error) => {
          console.error("Failed to persist custom doc markdown", error);
        });
      },
      [persistEditorMarkdown]
    );

    // Expose imperative methods for parent components to reload content
    useImperativeHandle(
      ref,
      () => ({
        reloadContent: (newMarkdown: string) => {
          editor.update(() => {
            importMarkdownToLexical(newMarkdown, PLAYGROUND_TRANSFORMERS, {
              preserveEmptyParagraphs: true,
            });
          });
        },
        getMarkdownContent: () => {
          return getCurrentMarkdownContent();
        },
      }),
      [editor, getCurrentMarkdownContent]
    );

    // Only import markdown on initial mount
    useEffect(() => {
      editor.update(
        () => {
          importMarkdownToLexical(markdown, PLAYGROUND_TRANSFORMERS, {
            preserveEmptyParagraphs: true,
          });
        },
        { tag: "initial-import" }
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    const editorContent = (
      <TableContext>
        <Box
          className="editor-container"
          sx={{
            maxWidth: "100%",
            overflowX: "hidden",
            position: "relative",
            width: "auto",
            padding: "16px",
            border: "1px solid #d0d7de",
            borderRadius: "8px",
            height: "100%",
            backgroundColor: "white",

            "& .tableScrollableWrapper": {
              marginBottom: "16px",
            },

            "& .tableScrollableWrapper:last-child": {
              marginBottom: "0",
            },

            ...(removeBorder && {
              border: "none",
              padding: "0",
            }),

            "& .editor-input": {
              height: "100%",
              padding: "16px",
            },

            ...(!hideToolbar && {
              padding: "0",
              maxWidth: "100%",
              overflowX: "hidden",
              width: "100%",
              minWidth: 0,
              "& .editor-inner": {
                maxWidth: "100%",
                overflowX: "hidden",
                width: "100%",
                minWidth: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              },
              "& .editor-inner .editor-input": {
                maxWidth: "100%",
                overflowX: "hidden",
                width: "100%",
                minWidth: 0,
                height: "100%",
              },
            }),

            ...(hideToolbar && {
              height: "auto",
              "& > .editor-inner": {
                height: "fit-content",
              },
              "& > .editor-inner > .editor-input": {
                height: "fit-content",
                whiteSpace: "normal",
              },
              "& > .editor-inner > .editor-input > .editor-paragraph:not(:last-child)":
                {
                  marginBottom: "16px",
                },
              "& > .editor-inner > .editor-input > .editor-paragraph > span:last-child":
                {
                  paddingBottom: "0",
                },
              "& .editor-inner .editor-input table": {
                tableLayout: "auto",
                ...(tableStyles || {}),
                width: tableStyles?.width || "100%",
                maxWidth: tableStyles?.maxWidth || "100%",
                minWidth: "0",
                display: "block",
                overflowX: "auto",
                boxSizing: "border-box",
                border: "none",
                "& tr > td > ul": {
                  padding: "inherit",
                },
                "& tr > td > ol": {
                  padding: "inherit",
                },
              },
            }),
          }}
        >
          <div className="editor-inner">
            <ToolbarContainer hideToolbar={hideToolbar} />
            {placeholder ? (
              <div
                style={{
                  position: "relative",
                  ...(!hideToolbar
                    ? { flex: 1, minHeight: 0 }
                    : { height: "100%" }),
                }}
              >
                <RichTextPlugin
                  contentEditable={
                    <ContentEditable className="editor-input outline-none" />
                  }
                  placeholder={
                    <div className="editor-placeholder">{placeholder}</div>
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
              </div>
            ) : (
              <RichTextPlugin
                contentEditable={
                  <ContentEditable className="editor-input outline-none" />
                }
                placeholder={null}
                ErrorBoundary={LexicalErrorBoundary}
              />
            )}
            <ListPlugin />
            <HistoryPlugin />
            {!isReadOnly && autoFocus && <AutoFocusPlugin />}
            <TabFocusPlugin />
            <TabIndentationPlugin maxIndent={7} />
            <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            <MarkdownPastePlugin />
            {!isReadOnly && orgId && <ImageUploadPlugin />}
            {!isReadOnly && (
              <AutoSavePlugin
                onChange={handleEditorChange}
                triggerAutosaveRef={triggerAutosaveRef}
              />
            )}
            <TablePlugin hasHorizontalScroll={true} />
            <TableHoverActionsPlugin />
            <TableCellResizer />
            <TableActionMenuPlugin />
            <TableCellSelectionPlugin />
            <TableCellLineBreakPlugin />
            {children}
          </div>
        </Box>
      </TableContext>
    );

    return (
      <AutosaveContext.Provider
        value={{
          isAutosaving: isLocalAutosaving,
          setIsAutosaving,
          triggerAutosave,
        }}
      >
        <ImageUploadProvider orgId={orgId}>{editorContent}</ImageUploadProvider>
      </AutosaveContext.Provider>
    );
  }
);

const RawEditor = forwardRef<RawEditorHandle, RawEditorProps>(
  ({ isReadOnly = false, ...props }, ref) => {
    const composerConfig = {
      ...editorConfig,
      editable: !isReadOnly,
    };

    return (
      <LexicalComposer initialConfig={composerConfig}>
        <RawEditorInner {...props} isReadOnly={isReadOnly} ref={ref} />
      </LexicalComposer>
    );
  }
);

export default RawEditor;

// Export RawEditorInner for tests that need to control the LexicalComposer lifecycle
export { RawEditorInner };
